/**
 * FloorPlanRenderTypeSelect — native lift of the legacy `FloorPlanRenderTypeController`
 * (2D/3D pick + Back). Data wiring is rewritten onto the ops-hook callbacks.
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useTranslation } from 'react-i18next'

import { BEIGE_500, GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MUIButton } from 'components/common/MUIButton'

import basic_furniture_3D from 'assets/img/floorplan/3D/3D_furniture_variant1.webp'
import basic_furniture_black_2D from 'assets/img/floorplan/2D/2D_commercial_bw_furniture.webp'

import { FloorPlanRenderType } from 'constants/floorPlan'
import { SelectableImageBox } from 'components/common/FloorPlanControls'

interface Props {
  is3DAllowed: boolean
  currentRenderType: FloorPlanRenderType
  selectRenderType: (renderType: FloorPlanRenderType) => void
  goBack: () => void
}

/**
 * @component FloorPlanRenderTypeSelect
 * 2D/3D render-type picker + Back to template selection.
 */
export const FloorPlanRenderTypeSelect: React.FC<Props> = ({ is3DAllowed, currentRenderType, selectRenderType, goBack }) => {
  const { t } = useTranslation(['purchase_flow', 'actions'])

  return (
    <BorderBoxWrapper backgroundColor={BEIGE_500} padding="2.4rem" elevation="none" border="none">

      <Stack alignItems="center" position="relative">

        <MUIButton
          isIconButton
          type="secondaryNoBorder"
          size="xs"
          startIcon={<ArrowBackRoundedIcon />}
          onClick={goBack}
          sx={{ position: 'absolute', left: 0, top: 0 }}
        >
          {t('actions:Back')}
        </MUIButton>

        <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
          {t('floor_plan_step.render_type.title')}
        </Typography>

      </Stack>

      <Stack direction="row" justifyContent="center" alignItems="center" gap={1.6} marginTop={2}>

        <SelectableImageBox
          text={t('floor_plan_step.render_type.2D')}
          height={14.4}
          width={16.8}
          elevation="xs"
          inactiveBorderColor="transparent"
          image={basic_furniture_black_2D}
          isSelected={currentRenderType === FloorPlanRenderType.RENDER_2D}
          onSelect={() => selectRenderType(FloorPlanRenderType.RENDER_2D)}
        />

        {is3DAllowed &&
          <SelectableImageBox
            text={t('floor_plan_step.render_type.3D')}
            height={14.4}
            width={16.8}
            elevation="xs"
            inactiveBorderColor="transparent"
            image={basic_furniture_3D}
            isSelected={currentRenderType === FloorPlanRenderType.RENDER_3D}
            onSelect={() => selectRenderType(FloorPlanRenderType.RENDER_3D)}
          />
        }

      </Stack>

    </BorderBoxWrapper>
  )
}
